
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface UserStats {
  posts: number;
  comments: number;
  followers: number;
  following: number;
  reputation: number;
  groups: number;
}

export function useProfileStats(userId: string | undefined) {
  const [stats, setStats] = useState<UserStats>({
    posts: 0,
    comments: 0,
    followers: 0,
    following: 0,
    reputation: 0,
    groups: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!userId) return;

    try {
      const [postsRes, commentsRes, followersRes, followingRes, groupsRes, profileRes] = await Promise.all([
        supabase.from('posts').select('id', { count: 'exact', head: true }).eq('author_id', userId),
        supabase.from('comments').select('id', { count: 'exact', head: true }).eq('author_id', userId),
        supabase.from('user_follows').select('id', { count: 'exact', head: true }).eq('following_id', userId),
        supabase.from('user_follows').select('id', { count: 'exact', head: true }).eq('follower_id', userId),
        supabase.from('group_members').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('user_profiles').select('reputation, followers_count, following_count').eq('id', userId).maybeSingle(),
      ]);

      setStats({
        posts: postsRes.count ?? 0,
        comments: commentsRes.count ?? 0,
        followers: profileRes.data?.followers_count ?? followersRes.count ?? 0,
        following: profileRes.data?.following_count ?? followingRes.count ?? 0,
        reputation: profileRes.data?.reputation ?? 0,
        groups: groupsRes.count ?? 0,
      });
    } catch (err) {
      console.error('Error fetching profile stats:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Real-time subscriptions
  useEffect(() => {
    if (!userId) return;

    const channels: ReturnType<typeof supabase.channel>[] = [];

    // Posts changes
    const postsCh = supabase
      .channel(`profile-posts-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts', filter: `author_id=eq.${userId}` }, () => {
        supabase.from('posts').select('id', { count: 'exact', head: true }).eq('author_id', userId)
          .then(({ count }) => setStats(prev => ({ ...prev, posts: count ?? prev.posts })));
      })
      .subscribe();
    channels.push(postsCh);

    // Comments changes
    const commentsCh = supabase
      .channel(`profile-comments-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'comments', filter: `author_id=eq.${userId}` }, () => {
        supabase.from('comments').select('id', { count: 'exact', head: true }).eq('author_id', userId)
          .then(({ count }) => setStats(prev => ({ ...prev, comments: count ?? prev.comments })));
      })
      .subscribe();
    channels.push(commentsCh);

    // Followers changes (someone follows/unfollows this user)
    const followersCh = supabase
      .channel(`profile-followers-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_follows', filter: `following_id=eq.${userId}` }, () => {
        supabase.from('user_follows').select('id', { count: 'exact', head: true }).eq('following_id', userId)
          .then(({ count }) => setStats(prev => ({ ...prev, followers: count ?? prev.followers })));
      })
      .subscribe();
    channels.push(followersCh);

    // Following changes (this user follows/unfollows someone)
    const followingCh = supabase
      .channel(`profile-following-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_follows', filter: `follower_id=eq.${userId}` }, () => {
        supabase.from('user_follows').select('id', { count: 'exact', head: true }).eq('follower_id', userId)
          .then(({ count }) => setStats(prev => ({ ...prev, following: count ?? prev.following })));
      })
      .subscribe();
    channels.push(followingCh);

    // Groups changes
    const groupsCh = supabase
      .channel(`profile-groups-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_members', filter: `user_id=eq.${userId}` }, () => {
        supabase.from('group_members').select('id', { count: 'exact', head: true }).eq('user_id', userId)
          .then(({ count }) => setStats(prev => ({ ...prev, groups: count ?? prev.groups })));
      })
      .subscribe();
    channels.push(groupsCh);

    // Profile reputation changes
    const profileCh = supabase
      .channel(`profile-rep-${userId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'user_profiles', filter: `id=eq.${userId}` }, (payload) => {
        const newData = payload.new as any;
        setStats(prev => ({
          ...prev,
          reputation: newData.reputation ?? prev.reputation,
          followers: newData.followers_count ?? prev.followers,
          following: newData.following_count ?? prev.following,
        }));
      })
      .subscribe();
    channels.push(profileCh);

    return () => {
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, [userId]);

  return { stats, loading, refetch: fetchStats };
}
